import mapboxgl from 'mapbox-gl';

import { selectScenarioName } from 'state/detourSlices';
import { useAppSelector } from 'state/hooks';
import {
  selectCurrentScenarioId,
  selectSegmentId,
  selectTargetDate,
  selectTimeOfDay,
  selectTypicalMonth,
  selectWorkflow,
} from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import { Segment } from 'appTypes';
import dayjs from 'dayjs';
import { formatComparisonMonthName } from 'features/chart/nrtChartCommon';
import {
  formatDateOrDateRange,
  formatTimeOfDay,
  parseDate,
} from 'features/common/datetimeUtils';
import { setVisIfNeeded } from 'features/map/utils/mapCommon';
import { kSegmentLayerId } from 'features/map/utils/map_constants';
import { WORKFLOW_PLAN_NAMES } from 'features/nav_bar/NavBar';
import { isComparisonMonth } from 'features/task_bar/ComparisonPicker';
import FileSaver from 'file-saver';
import { toPng } from 'html-to-image';

import { useRouteInfo } from './hooks/useRouteInfo';
import { showToast } from './toasts/ToastProvider';

export enum ExportType {
  Map = 'Map',
  Chart = 'Chart',
  Data = 'Data',
}

const PERIOD_BASED_WORKFLOWS = [
  WORKFLOW_PLAN_NAMES[WorkflowType.Plan],
  WORKFLOW_PLAN_NAMES[WorkflowType.Detouring],
];

const EXPORT_SUCCESS_MESSAGE = 'File exported successfully.';
const EXPORT_FAILURE_MESSAGE = 'Failed to export file.';

function getWorkflowKey(workflow: WorkflowType): string {
  return WORKFLOW_PLAN_NAMES[workflow].toLowerCase().replace(' ', '');
}

export function useExportFilename(type: ExportType): string {
  const workflow = useAppSelector(selectWorkflow);
  const targetDate = useAppSelector(selectTargetDate);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const segmentId = useAppSelector(selectSegmentId);
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const selectedScenario = useAppSelector(selectScenarioName);
  const { savedRoute } = useRouteInfo();
  const id =
    (currentScenarioId && selectedScenario) ||
    (savedRoute
      ? savedRoute.routeName.replace(/ /g, '_').toLowerCase()
      : segmentId);

  const parts: string[] = [getWorkflowKey(workflow)];

  const isPeriodBased = PERIOD_BASED_WORKFLOWS.includes(
    WORKFLOW_PLAN_NAMES[workflow],
  );

  if (typicalMonth && isPeriodBased) {
    parts.push(typicalMonth.replace('-', '_'));
  } else if (targetDate) {
    parts.push(parseDate(targetDate).format('MMDDYYYY'));
  }
  if (timeOfDay) parts.push(formatTimeOfDay(timeOfDay).replace(':', ''));
  if (id) parts.push(id);
  if (type !== ExportType.Data) parts.push(type.toLowerCase());
  const suffix = type === ExportType.Data ? '.csv' : '';
  return `${parts.join('_')}${suffix}`;
}

export function nrtCommonInfo(
  targetDate: string,
  comparison: string,
  segment: Segment,
  uses_middle_east_day_types: boolean,
  targetEndDate?: string,
  comparisonEndDate?: string,
) {
  const targetFmt = formatDateOrDateRange(
    targetDate,
    targetEndDate,
    'YYYY-MM-DD',
  );
  let comparisonFmt = comparison;
  if (isComparisonMonth(comparison)) {
    comparisonFmt = formatComparisonMonthName(
      comparison,
      targetDate,
      uses_middle_east_day_types,
    );
  } else {
    comparisonFmt = formatDateOrDateRange(
      comparison,
      comparisonEndDate,
      'YYYY-MM-DD',
    );
  }
  return [
    segment.properties.cardinal_dir
      ? `Direction, ${segment.properties.cardinal_dir}`
      : null,
    `Date, ${targetFmt}`,
    `Comparison, ${comparisonFmt}`,
    `Access date, ${dayjs().format('YYYY-MM-DD')}`,
  ];
}

function triggerBlobExport(file: string, type: string, filename: string) {
  try {
    const blob = new Blob([file], {
      type,
    });
    FileSaver.saveAs(blob, filename);
    showToast('success', EXPORT_SUCCESS_MESSAGE);
  } catch (e) {
    console.error(e);
    showToast('error', EXPORT_FAILURE_MESSAGE);
  }
}

export function triggerCsvExport(filename: string, contents: any[]) {
  triggerBlobExport(
    `\ufeff${contents.filter((i) => i !== null).join('\r\n')}`,
    'text/plain;charset=utf-8',
    filename,
  );
}

export function triggerGeojsonExport(filename: string, geojson: object) {
  triggerBlobExport(JSON.stringify(geojson), 'application/geo+json', filename);
}

const downloadScreenshot = async (
  node: HTMLElement,
  fileName: string,
): Promise<void> => {
  if (!node || !fileName) return;

  try {
    const dataUrl = await toPng(node);
    const link = document.createElement('a');
    link.download = `${fileName}.png`;
    link.href = dataUrl;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', EXPORT_SUCCESS_MESSAGE);
  } catch (error) {
    console.error(EXPORT_FAILURE_MESSAGE, error);
    showToast('error', EXPORT_FAILURE_MESSAGE);
  }
};

function setElementsDisplay(
  elements: NodeListOf<Element>,
  display: string,
): void {
  elements.forEach((element) => {
    (element as HTMLElement).style.display = display;
  });
}

export const downloadMapScreenshot = async (
  map: mapboxgl.Map,
  fileName: string,
  hideSegmentLayer = true,
): Promise<void> => {
  if (!map) return;
  if (hideSegmentLayer) setVisIfNeeded(map, 'none', kSegmentLayerId);
  const node = map?.getContainer();
  if (!node) return;
  const hiddenControls = node.querySelectorAll(
    '.mapboxgl-ctrl, [data-hide-in-export]',
  );
  const shownControls = node.querySelectorAll('[data-show-in-export]');

  try {
    setElementsDisplay(hiddenControls, 'none');
    setElementsDisplay(shownControls, 'block');
    // wait for DOM repaint
    await new Promise((r) => setTimeout(r, 100));
    await downloadScreenshot(node, fileName);
  } finally {
    setElementsDisplay(hiddenControls, '');
    setElementsDisplay(shownControls, 'none');
    if (hideSegmentLayer) setVisIfNeeded(map, 'visible', kSegmentLayerId);
  }
};
export const screenshotContainerClass = 'screenshotChart';

export const downloadChartScreenshot = async (
  fileName: string,
): Promise<void> => {
  const node = document.querySelector(
    `.${screenshotContainerClass}`,
  ) as HTMLElement | null;
  if (!node) {
    showToast('error', 'Chart element not found.');
    return;
  }

  await downloadScreenshot(node, fileName);
};
